import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { HINWEIS_EBENE_FEHLT, HINWEIS_RUECKFALL } from '../config/ebenen';

interface ProtectedRouteProps {
  children: React.ReactNode;
  // plattform = Verwaltung, firma = Admin-Einstellungen (SPEC VE-06); beide aus dem Ebenenobjekt des Servers.
  requiredAccess?: 'platform-admin' | 'plattform' | 'firma';
}

// Stiller Rückfall mit kurzem Hinweis (VE-06, M-18.3).
function Rueckfall({ to, hinweis }: { to: string; hinweis: string }) {
  useEffect(() => { toast.info(hinweis, { id: 'ebene-rueckfall' }); }, [hinweis]);
  return <Navigate to={to} replace />;
}

function ProtectedRoute({ children, requiredAccess }: ProtectedRouteProps) {
  const { isAuthenticated, isAdmin, user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-warm-bg flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          <p className="text-muted">Laden...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Redirect to login, preserving the intended destination
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (requiredAccess === 'platform-admin' && !isAdmin) {
    // Redirect non-admins to main app
    return <Navigate to="/" replace />;
  }

  const ebenen = user?.ebenen;
  // Paket 08 Schritt 6 (A-7): ohne Ebenenobjekt derselbe Bereich wie das Serverziel.
  const meine = ebenen?.meineEinstellungen?.ziel || '/einstellungen/zugang';
  if (requiredAccess === 'plattform' && !ebenen?.verwaltung) {
    // E-VE-9: die alte Adresse /verwaltung führt Firmenadmins in ihre Admin-Einstellungen, Parameter bleiben.
    if (ebenen?.adminEinstellungen) return <Rueckfall to={ebenen.adminEinstellungen.ziel + location.search} hinweis={HINWEIS_RUECKFALL} />;
    return <Rueckfall to={meine} hinweis={HINWEIS_EBENE_FEHLT} />;
  }
  if (requiredAccess === 'firma' && !ebenen?.adminEinstellungen) {
    return <Rueckfall to={ebenen?.verwaltung?.ziel || meine} hinweis={HINWEIS_EBENE_FEHLT} />;
  }

  return <>{children}</>;
}

export default ProtectedRoute;
